import { useEffect, useMemo, useState } from "react";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AgGridAtom, ModalAtom } from "../../../components/atoms/atom";
import { FilterBar } from "../../../components/molecule/molecule";
import ProjectHeader from "../../../components/ProjectHeader";
import NoPermissionAlert from "../../../components/NoPermissionAlert";
import useProjectPermission from "../../../hooks/useProjectPermission";
import {
	useAddProjectCompaniesMutation,
	useCreateProjectCompanyMutation,
	useDeleteProjectCompanyMutation,
	useGetProjectCompaniesQuery,
	useUpdateProjectCompanyMutation,
} from "../../../store/services/projectCompaniesService";
import { getSearchAddFilter } from "../../../utils/get-filter-items";
import { createActionColumn } from "../../../utils/grid-columns";
import {
	getFormattedDate,
	showError,
	showSuccess,
} from "../../../utils/method";
import { searchData } from "../../../utils/search";
import AddCompanyModal from "./add-company-modal";
import AddProjectCompanyModal from "./add-project-company-modal";
import EditProjectCompanyModal from "./edit-project-company-modal";
import { useTranslation } from "react-i18next";
import capmoLogo from "../../../assets/images/capmo_logo.svg";
import "./project-companies.scss";
import TableSkeleton from "../../../components/atoms/table-skeleton/TableSkeleton";
import { PROJECT_AREA, PROJECT_ACTION } from "../../../constant/projectPermissions";

const buildCompanyFilterItems = (canEdit) =>
	getSearchAddFilter(
		canEdit
			? {
				addDropdownButton: {
					labelKey: "projects.companies.add",
					variant: "success",
					key: "add-company",
					options: [
						{
							labelKey: "projects.companies.select-from-master-data",
							icon: "bi bi-building",
							key: "select-from-master",
							value: "select-from-master",
						},
						{
							labelKey: "projects.companies.add-project-company",
							icon: "bi bi-plus-circle",
							key: "add-project-company",
							value: "add-project-company",
						},
					],
				},
			}
			: { hideAddButton: true },
	);

const getCompanyColumns = (t, onEdit, onDelete, canEdit, canDelete) => [
	{
		field: "createdAt",
		headerName: t("projects.provided"),
		headerTooltip: t("projects.provided"),
		unSortIcon: true,
		valueFormatter: (params) => getFormattedDate(params.value),
		width: 150,
	},
	{
		field: "company.isFromCapmo",
		headerName: t("common.source"),
		headerTooltip: t("common.source"),
		unSortIcon: true,
		sortable: false,
		cellRenderer: (params) => {
			const company = params.data?.company;
			if (!company) {
				return null;
			}

			if (company.capmoId) {
				return (
					<img
						src={capmoLogo}
						alt="CAPMO"
						height="16"
						width="16"
						title={t("lv.from-capmo")}
						className="d-inline-block"
					/>
				);
			}

			return (
				<FontAwesomeIcon
					icon={faHome}
					title={t("projects.local-project")}
					className="fs-6"
				/>
			);
		},
		maxWidth: 80,
	},
	{
		field: "company.name",
		headerName: t("companies.name"),
		headerTooltip: t("companies.name"),
		unSortIcon: true,
		valueGetter: (params) => params.data.company?.name || "",
		flex: 2,
	},
	{
		field: "company.address",
		headerName: t("companies.address"),
		headerTooltip: t("companies.address"),
		unSortIcon: true,
		valueGetter: (params) => {
			const company = params.data.company;
			if (!company) return "-";
			const addressParts = [
				company.addressStreet,
				company.addressZipCode,
				company.addressCity,
				company.addressCountry,
			].filter(Boolean);
			return addressParts.length > 0 ? addressParts.join(", ") : "-";
		},
		flex: 2,
		minWidth: 250,
	},
	{
		field: "company.phoneNumber",
		headerName: t("companies.phone"),
		headerTooltip: t("companies.phone"),
		unSortIcon: true,
		valueGetter: (params) => params.data.company?.phoneNumber || "-",
		width: 150,
	},
	createActionColumn({
		t,
		actions: [
			{
				onClick: onEdit,
				label: "help.action-edit",
				// Needs the companies "edit" permission, and only for
				// project-only companies (CAPMO master data is never editable
				// here).
				show: (data) =>
					canEdit &&
					!!(data.company?.isProjectOnly && !data.company?.capmoId),
			},
			{
				onClick: onDelete,
				label: "help.action-delete",
				danger: true,
				show: () => canDelete,
			},
		],
	}),
];

function ProjectCompanies({ projectId }) {
	const { t } = useTranslation();
	const paramProjectId = useParams().projectId || projectId;

	const projectDetails = useSelector(
		(state) => state.projectDetails.project,
	);

	const { can, isLoading: permissionLoading } = useProjectPermission();
	const canViewCompanies = !permissionLoading && can(PROJECT_AREA.COMPANIES, PROJECT_ACTION.VIEW);
	// Company writes are gated on the per-area create / edit / delete actions
	// (project admins implicitly pass via can()).
	const canCreateCompany = can(PROJECT_AREA.COMPANIES, PROJECT_ACTION.CREATE);
	const canEditCompany = can(PROJECT_AREA.COMPANIES, PROJECT_ACTION.EDIT);
	const canDeleteCompany = can(PROJECT_AREA.COMPANIES, PROJECT_ACTION.DELETE);

	const {
		data: projectCompaniesResponse,
		isLoading,
	} = useGetProjectCompaniesQuery(paramProjectId, {
		skip: !paramProjectId || !canViewCompanies,
	});
	const projectCompanies = useMemo(
		() => projectCompaniesResponse?.items ?? [],
		[projectCompaniesResponse?.items],
	);

	const [addProjectCompanies] = useAddProjectCompaniesMutation();
	const [createProjectCompany] = useCreateProjectCompanyMutation();
	const [updateProjectCompany] = useUpdateProjectCompanyMutation();
	const [deleteProjectCompany] = useDeleteProjectCompanyMutation();

	const [filteredCompanies, setFilteredCompanies] = useState([]);
	const [showAddModal, setShowAddModal] = useState(false);
	const [showAddProjectCompanyModal, setShowAddProjectCompanyModal] =
		useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [selectedCompany, setSelectedCompany] = useState(null);
	const [creatingCompany, setCreatingCompany] = useState(false);
	const [updatingCompany, setUpdatingCompany] = useState(false);
	const [deletingCompany, setDeletingCompany] = useState(false);

	const [filterItems, setFilterItems] = useState(() =>
		buildCompanyFilterItems(canCreateCompany),
	);

	useEffect(() => {
		setFilterItems((prev) => {
			const searchValue =
				prev.find((i) => i.key === "search")?.value || "";
			const next = buildCompanyFilterItems(canCreateCompany);
			const searchItem = next.find((i) => i.key === "search");
			if (searchItem) searchItem.value = searchValue;
			return next;
		});
	}, [canCreateCompany]);

	// Update filtered companies when companies change
	useEffect(() => {
		setFilteredCompanies(projectCompanies);
	}, [projectCompanies]);

	const handleEdit = (data) => {
		setSelectedCompany(data.company);
		setShowEditModal(true);
	};

	const handleDelete = (data) => {
		setSelectedCompany(data);
		setShowDeleteModal(true);
	};

	const companyColumns = getCompanyColumns(
		t,
		handleEdit,
		handleDelete,
		canEditCompany,
		canDeleteCompany,
	);

	const handleAddCompany = async (companyIds) => {
		try {
			const result = await addProjectCompanies({
				projectId: paramProjectId,
				companyIds,
			}).unwrap();

			const { added, skipped, total } = result.data || {};
			if (added > 0) {
				if (added === 1) {
					showSuccess(
						t,
						t("projects.companies.company-added-success"),
					);
				} else {
					showSuccess(
						t,
						t("projects.companies.companies-added-success", {
							count: added,
							total,
						}),
					);
				}
			} else if (skipped === total) {
				showSuccess(
					t,
					t("projects.companies.all-companies-already-added"),
				);
			}

			setShowAddModal(false);
		} catch (error) {
			showError(t, error);
		}
	};

	const handleCreateProjectCompany = async (companyData, allowDuplicateName) => {
		try {
			setCreatingCompany(true);
			await createProjectCompany({
				projectId: paramProjectId,
				...companyData,
				...(allowDuplicateName ? { allowDuplicateName: true } : {}),
			}).unwrap();

			showSuccess(
				t,
				t("projects.companies.project-company-created-success"),
			);

			setShowAddProjectCompanyModal(false);
		} catch (error) {
			showError(t, error);
		} finally {
			setCreatingCompany(false);
		}
	};

	// Picking an existing company from the inline modal's name typeahead links
	// it to the project instead of creating a duplicate.
	const handleSelectExistingCompany = async (companyId) => {
		try {
			setCreatingCompany(true);
			await addProjectCompanies({
				projectId: paramProjectId,
				companyIds: [companyId],
			}).unwrap();

			showSuccess(
				t,
				t("projects.companies.company-added-success"),
			);

			setShowAddProjectCompanyModal(false);
		} catch (error) {
			showError(t, error);
		} finally {
			setCreatingCompany(false);
		}
	};

	const handleUpdateProjectCompany = async (companyData) => {
		try {
			setUpdatingCompany(true);
			await updateProjectCompany({
				projectId: paramProjectId,
				companyId: selectedCompany.id,
				...companyData,
			}).unwrap();

			showSuccess(
				t,
				t("projects.companies.project-company-updated-success"),
			);

			setShowEditModal(false);
			setSelectedCompany(null);
		} catch (error) {
			showError(t, error);
		} finally {
			setUpdatingCompany(false);
		}
	};

	const handleDeleteProjectCompany = async () => {
		try {
			setDeletingCompany(true);
			await deleteProjectCompany({
				projectId: paramProjectId,
				projectCompanyId: selectedCompany.id,
			}).unwrap();

			showSuccess(
				t,
				t("projects.companies.project-company-deleted-success"),
			);

			setShowDeleteModal(false);
			setSelectedCompany(null);
		} catch (error) {
			showError(t, error);
		} finally {
			setDeletingCompany(false);
		}
	};

	const existingCompanyIds = projectCompanies.map((pc) => pc.company?.id);
	// Names already linked to this project - lets the inline create modal detect a
	// same-name company that lives only in this project (those aren't returned by
	// the master-data name search).
	const existingCompanyNames = projectCompanies
		.map((pc) => pc.company?.name)
		.filter(Boolean);

	const onFilterAction = (index, key, info) => {
		const localFilterItems = [...filterItems];
		localFilterItems[index].value = info?.value;
		setFilterItems(localFilterItems);

		// Handle dropdown button actions
		if (key === "select-from-master") {
			setShowAddModal(true);
			return;
		}

		if (key === "add-project-company") {
			setShowAddProjectCompanyModal(true);
			return;
		}

		let filtered = [...projectCompanies];

		// Apply all active filters
		localFilterItems.forEach((item) => {
			if (item.key === "search" && item.value) {
				filtered = searchData(item.value, filtered, companyColumns);
			}
		});

		setFilteredCompanies(filtered);
	};

	if (permissionLoading) {
		return (
			<div className="project-companies-page">
				<ProjectHeader
					projectName={projectDetails?.name}
					tabTitle={t("projects.companies.title")}
				/>
				<div className="page-container">
					<TableSkeleton />
				</div>
			</div>
		);
	}

	if (!canViewCompanies) {
		return (
			<div className="project-companies-page">
				<ProjectHeader
					projectName={projectDetails?.name}
					tabTitle={t("projects.companies.title")}
				/>
				<NoPermissionAlert />
			</div>
		);
	}

	return (
		<div className="project-companies-page">
			<ProjectHeader
				projectName={projectDetails?.name}
				tabTitle={t("projects.companies.title")}
			/>
			<div className="page-container">
				<FilterBar
					items={filterItems}
					onFilterAction={onFilterAction}
					canAdd={canCreateCompany}
				/>

				<div className="flex-grow-1 ag-grid-parent">
					{isLoading && filteredCompanies.length === 0 ? (
						<TableSkeleton />
					) : (
						<AgGridAtom
							columnDefs={companyColumns}
							rowData={filteredCompanies}
							context={{ projectCan: can }}
							overlayLoadingTemplate=""
							overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${t("projects.companies.no-companies")}</span>`}
							defaultColDef={{
								sortable: true,
								filter: false,
								resizable: true,
							}}
						/>
					)}
				</div>
			</div>

			<AddCompanyModal
				show={showAddModal}
				onClose={() => setShowAddModal(false)}
				onSubmit={handleAddCompany}
				existingCompanyIds={existingCompanyIds}
			/>

			<AddProjectCompanyModal
				show={showAddProjectCompanyModal}
				onClose={() => setShowAddProjectCompanyModal(false)}
				onSubmit={handleCreateProjectCompany}
				loading={creatingCompany}
				existingCompanyIds={existingCompanyIds}
				existingCompanyNames={existingCompanyNames}
				onSelectExisting={handleSelectExistingCompany}
			/>

			<EditProjectCompanyModal
				show={showEditModal}
				onClose={() => {
					setShowEditModal(false);
					setSelectedCompany(null);
				}}
				onSubmit={handleUpdateProjectCompany}
				loading={updatingCompany}
				companyData={selectedCompany}
			/>

			<ModalAtom
				show={showDeleteModal}
				handleClose={() => {
					setShowDeleteModal(false);
					setSelectedCompany(null);
				}}
				title={t("projects.companies.delete-company")}
				handleSubmit={handleDeleteProjectCompany}
				saveText={deletingCompany ? "common.deleting" : "common.delete"}
				saveButtonVariant="danger"
				body={
					<p>
						{t("projects.companies.delete-company-confirmation", {
							name: selectedCompany?.company?.name || "",
						})}
					</p>
				}
			/>
		</div>
	);
}

export default ProjectCompanies;
